import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  Info,
  ThumbsDown,
  ThumbsUp,
} from "lucide-react";
import { fetchCategories, fetchMyCategoryGamesPlayed } from "../../../api/categoryService";
import {
  fetchVotingImageProposals,
  voteImageProposal,
} from "../../../api/communityService";
import { useAuth } from "../../../context/AuthContext";
import { API_BASE_URL } from "../../../utils/constants";
import RoomCard from "./RoomCard";

const PROPOSAL_LINK = "/profile/image-proposals";
const LOGIN_LINK = "/authentification#connexion";
const MIN_GAMES_TO_VOTE = 25;

function formatDate(value) {
  if (!value) return "";
  return new Date(value).toLocaleDateString("fr-FR");
}

function proposalCanBeShown(proposal, isAuthenticated) {
  if (proposal?.status !== "voting") {
    return false;
  }

  if (!isAuthenticated) {
    return true;
  }

  return proposal.can_vote !== false && !proposal.has_voted && !proposal.current_vote;
}

function requestWasCanceled(error) {
  return (
    error?.name === "CanceledError" ||
    error?.code === "ERR_CANCELED" ||
    String(error?.message || "").toLowerCase() === "canceled"
  );
}

function normalizeImageUrl(value) {
  if (!value || typeof window === "undefined") {
    return value;
  }

  try {
    const imageUrl = new URL(value, window.location.origin);
    const apiUrl = new URL(API_BASE_URL, window.location.origin);
    if (
      window.location.protocol === "https:" &&
      imageUrl.protocol === "http:" &&
      imageUrl.hostname === apiUrl.hostname
    ) {
      imageUrl.protocol = "https:";
    }
    return imageUrl.toString();
  } catch (_error) {
    return value;
  }
}

export default function RoomImageProposals({
  categorySlug,
  categoryName,
  isAuthenticated,
  gamesCompletedTick,
}) {
  const { user } = useAuth();
  const isPrivileged = Boolean(
    user?.is_staff || user?.is_superuser || user?.is_moderator
  );
  const [proposals, setProposals] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [isVoting, setIsVoting] = useState(false);
  const [error, setError] = useState("");
  const [proposalRules, setProposalRules] = useState("");
  const [gamesPlayed, setGamesPlayed] = useState(null);
  const requestIdRef = useRef(0);
  const previousTickRef = useRef(gamesCompletedTick);

  const canVoteByGames = isPrivileged || (gamesPlayed ?? 0) >= MIN_GAMES_TO_VOTE;

  useEffect(() => {
    if (!isAuthenticated || !categorySlug || isPrivileged) {
      setGamesPlayed(isPrivileged ? MIN_GAMES_TO_VOTE : null);
      return undefined;
    }

    const controller = new AbortController();
    fetchMyCategoryGamesPlayed(categorySlug, controller.signal)
      .then((count) => setGamesPlayed(count))
      .catch(() => {});

    return () => controller.abort();
  }, [categorySlug, isAuthenticated, isPrivileged]);

  useEffect(() => {
    if (
      gamesCompletedTick === undefined ||
      gamesCompletedTick === previousTickRef.current
    ) {
      previousTickRef.current = gamesCompletedTick;
      return;
    }
    previousTickRef.current = gamesCompletedTick;
    if (isPrivileged) return;
    setGamesPlayed((current) => (current === null ? current : current + 1));
  }, [gamesCompletedTick, isPrivileged]);

  useEffect(() => {
    if (!categorySlug) {
      setProposalRules("");
      return undefined;
    }

    const controller = new AbortController();
    fetchCategories(controller.signal)
      .then((data) => {
        const categories = Array.isArray(data) ? data : [];
        const currentCategory = categories.find(
          (category) => category.slug === categorySlug
        );
        setProposalRules(currentCategory?.proposal_rules || "");
      })
      .catch(() => {
        setProposalRules("");
      });

    return () => controller.abort();
  }, [categorySlug]);

  useEffect(() => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;

    if (!categorySlug) {
      setProposals([]);
      setActiveIndex(-1);
      setError("");
      setIsLoading(false);
      return undefined;
    }

    const controller = new AbortController();
    setIsLoading(true);
    setError("");

    fetchVotingImageProposals(categorySlug, controller.signal)
      .then((data) => {
        if (requestIdRef.current !== requestId) {
          return;
        }

        const nextProposals = Array.isArray(data?.proposals)
          ? data.proposals.filter((proposal) =>
              proposalCanBeShown(proposal, isAuthenticated)
            )
          : [];
        setProposals(nextProposals);
        setActiveIndex(nextProposals.length > 0 ? 0 : -1);
        setError("");
      })
      .catch((loadError) => {
        if (
          requestIdRef.current !== requestId ||
          requestWasCanceled(loadError)
        ) {
          return;
        }

        setError("Impossible de charger les propositions.");
      })
      .finally(() => {
        if (requestIdRef.current === requestId) {
          setIsLoading(false);
        }
      });

    return () => controller.abort();
  }, [categorySlug, isAuthenticated]);

  const activeProposal = useMemo(() => {
    if (!proposals.length || activeIndex < 0) return null;
    return proposals[activeIndex] || null;
  }, [activeIndex, proposals]);

  const showEmptyState = !isLoading && (!activeProposal || proposals.length === 0);

  const goToPreviousProposal = () => {
    if (proposals.length <= 1) return;
    setActiveIndex((currentIndex) =>
      currentIndex <= 0 ? proposals.length - 1 : currentIndex - 1
    );
  };

  const goToNextProposal = () => {
    if (proposals.length <= 1) return;
    setActiveIndex((currentIndex) =>
      currentIndex >= proposals.length - 1 ? 0 : currentIndex + 1
    );
  };

  const submitVote = async (value) => {
    if (!activeProposal || isVoting) return;
    if (activeProposal.can_vote === false) {
      setError(
        activeProposal.has_voted
          ? "Votre vote est déjà pris en compte."
          : "Vous ne pouvez pas voter pour votre propre proposition."
      );
      return;
    }

    setIsVoting(true);
    setError("");

    try {
      await voteImageProposal(activeProposal.id, value);
      const updatedProposals = proposals.filter(
        (proposal) => proposal.id !== activeProposal.id
      );
      setProposals(updatedProposals);
      setActiveIndex(
        updatedProposals.length > 0
          ? Math.min(activeIndex, updatedProposals.length - 1)
          : -1
      );
    } catch (voteError) {
      const message =
        voteError instanceof Error ? voteError.message : "Vote impossible.";
      setError(message);
      if (
        /deja|déjà|propre proposition|already/i.test(message) &&
        activeProposal
      ) {
        const updatedProposals = proposals.filter(
          (proposal) => proposal.id !== activeProposal.id
        );
        setProposals(updatedProposals);
        setActiveIndex(
          updatedProposals.length > 0
            ? Math.min(activeIndex, updatedProposals.length - 1)
            : -1
        );
      }
    } finally {
      setIsVoting(false);
    }
  };

  const isLocked = isAuthenticated && !canVoteByGames;

  return (
    <RoomCard
      title="Images à voter"
      subtitle={`Propositions de ${categoryName || "cette categorie"}.`}
      contentClassName="space-y-4"
      allowOverflow
    >
      <div className="relative space-y-4">
      {isLocked ? (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center rounded-[28px] bg-white/70 backdrop-blur-sm">
          <div className="mx-4 rounded-2xl border border-slate-200 bg-white/90 px-4 py-3 text-center text-sm font-semibold text-slate-700 shadow-lg">
            <p>Joue {MIN_GAMES_TO_VOTE} parties dans cette categorie pour voter</p>
            <p className="mt-1 text-base font-bold text-slate-900">
              {gamesPlayed ?? 0}/{MIN_GAMES_TO_VOTE}
            </p>
          </div>
        </div>
      ) : null}
      <div className={`space-y-4 ${isLocked ? "pointer-events-none blur-sm" : ""}`}>
      <div className="flex items-center justify-between gap-3 rounded-2xl border border-sky-100 bg-sky-50 px-3 py-2 text-sm text-slate-700">
        <span className="font-semibold">Règles de proposition</span>
        <div className="group relative">
          <button
            type="button"
            className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-white text-sky-700 shadow-sm ring-1 ring-sky-100 transition hover:bg-sky-100"
            aria-label="Voir les regles de proposition"
          >
            <Info className="h-4 w-4" />
          </button>
          <div className="pointer-events-none absolute right-0 top-10 z-20 hidden w-72 rounded-2xl border border-sky-100 bg-white p-4 text-sm leading-6 text-slate-600 shadow-xl group-hover:block group-focus-within:block">
            {proposalRules ? (
              <p className="whitespace-pre-wrap">{proposalRules}</p>
            ) : (
              <p>Aucune regle specifique n'est renseignee pour cette categorie.</p>
            )}
          </div>
        </div>
      </div>

      {isLoading ? (
        <div className="rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-500">
          Chargement des propositions...
        </div>
      ) : null}

      {error ? (
        <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
          {error}
        </div>
      ) : null}

      {showEmptyState ? (
        <div className="space-y-3 rounded-3xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-sm text-slate-600">
          <p>Aucune image à voter pour le moment.</p>
          <a
            href={PROPOSAL_LINK}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-2 font-semibold text-cyan-700 hover:text-cyan-800"
          >
            Proposer une image
            <ExternalLink className="h-4 w-4" />
          </a>
        </div>
      ) : null}

      {activeProposal ? (
        <article className="overflow-hidden rounded-3xl border border-slate-200 bg-white">
          <div className="relative">
            {activeProposal.image_url ? (
              <img
                src={normalizeImageUrl(activeProposal.image_url)}
                alt={activeProposal.answer}
                className="h-40 w-full object-cover"
              />
            ) : null}

            {proposals.length > 1 ? (
              <>
                <button
                  type="button"
                  onClick={goToPreviousProposal}
                  disabled={isVoting}
                  className="absolute left-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-lg transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="Proposition precedente"
                >
                  <ChevronLeft className="h-5 w-5" />
                </button>
                <button
                  type="button"
                  onClick={goToNextProposal}
                  disabled={isVoting}
                  className="absolute right-2 top-1/2 inline-flex h-9 w-9 -translate-y-1/2 items-center justify-center rounded-full bg-white/90 text-slate-800 shadow-lg transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                  aria-label="Proposition suivante"
                >
                  <ChevronRight className="h-5 w-5" />
                </button>
                <div className="absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-slate-950/80 px-3 py-1 text-xs font-semibold text-white backdrop-blur">
                  {activeIndex + 1}/{proposals.length}
                </div>
              </>
            ) : null}
          </div>

          <div className="space-y-3 p-4">
            <div>
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400">
                Proposition
              </div>
              <h3 className="mt-1 text-lg font-semibold text-slate-950">
                {activeProposal.answer}
              </h3>
              {activeProposal.auto_review_at ? (
                <p className="mt-1 text-xs text-slate-500">
                  Vote ouvert jusqu'au {formatDate(activeProposal.auto_review_at)}
                </p>
              ) : null}
            </div>

            {isAuthenticated ? (
              activeProposal.can_vote === false ? (
                <div className="rounded-2xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm font-semibold text-slate-600">
                  {activeProposal.has_voted
                    ? "Votre vote est deja déjà en compte."
                    : "Vous ne pouvez pas voter pour votre propre proposition."}
                </div>
              ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  type="button"
                  onClick={() => submitVote(1)}
                  disabled={isVoting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-emerald-50 px-3 py-2.5 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ThumbsUp className="h-4 w-4" />
                  Oui {activeProposal.upvotes}
                </button>
                <button
                  type="button"
                  onClick={() => submitVote(-1)}
                  disabled={isVoting}
                  className="inline-flex items-center justify-center gap-2 rounded-2xl bg-rose-50 px-3 py-2.5 text-sm font-semibold text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  <ThumbsDown className="h-4 w-4" />
                  Non {activeProposal.downvotes}
                </button>
              </div>
              )
            ) : (
              <a
                href={LOGIN_LINK}
                target="_blank"
                rel="noreferrer"
                className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-950 px-4 py-3 text-sm font-semibold text-white transition hover:bg-slate-800"
              >
                Inscrit toi pour pouvoir voter
                <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </article>
      ) : null}
      </div>
      </div>
    </RoomCard>
  );
}

